import { vec3, mat4, type ReadonlyVec3 } from 'gl-matrix';
import { DoseMatrix } from '~/domain/patient/doseMatrix';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';

/* -------------------------------------------------------------
   Grid & transforms (mat4 only)
------------------------------------------------------------- */

export type GridXform = {
    dims: [number, number, number]; // [nx, ny, nz] x-fastest layout
    i2w: mat4; // world = i2w * [i,j,k,1]
    w2i: mat4; // index = w2i * [x,y,z,1]
};

export type DVHOptions = {
    threshold?: number; // default 127.5
    binWidthGy?: number; // default 0.1
    doseMinGy?: number; // default 0
    doseMaxGy?: number; // default auto from data
    edgeBand?: number; // NN band around threshold to trigger precise calc (default 6 U8 units)
};

export type DVHResult = {
    binWidthGy: number;
    doseMinGy: number;
    doseMaxGy: number;
    doseMeanGy: number;
    cumDvhRelative: Float64Array;
    structureVolume_mm3: number;
};

function emptyDVHResult(): DVHResult {
    return {
        binWidthGy: 0.1,
        doseMinGy: 0,
        doseMaxGy: 0,
        doseMeanGy: 0,
        cumDvhRelative: new Float64Array(),
        structureVolume_mm3: 0,
    };
}

/** Build index->world transform from origin, spacing, and axis unit vectors (in world). */
export function makeGridXform(
    dims: [number, number, number],
    originW: ReadonlyVec3, // world coord of voxel center (0,0,0)
    spacing: ReadonlyVec3, // voxel size in mm
    dirX: ReadonlyVec3, // world unit vector for grid +X
    dirY: ReadonlyVec3, // world unit vector for grid +Y
    dirZ: ReadonlyVec3 // world unit vector for grid +Z
): GridXform {
    const i2w = mat4.create();
    // Column-major: first 3 columns are basis vectors, last column is translation.
    // Basis vectors are axis * spacing.
    i2w[0] = dirX[0] * spacing[0];
    i2w[1] = dirX[1] * spacing[0];
    i2w[2] = dirX[2] * spacing[0];
    i2w[3] = 0;
    i2w[4] = dirY[0] * spacing[1];
    i2w[5] = dirY[1] * spacing[1];
    i2w[6] = dirY[2] * spacing[1];
    i2w[7] = 0;
    i2w[8] = dirZ[0] * spacing[2];
    i2w[9] = dirZ[1] * spacing[2];
    i2w[10] = dirZ[2] * spacing[2];
    i2w[11] = 0;
    i2w[12] = originW[0];
    i2w[13] = originW[1];
    i2w[14] = originW[2];
    i2w[15] = 1;

    const w2i = mat4.invert(mat4.create(), i2w)!;
    return { dims, i2w, w2i };
}

/* ------------------------ tiny helpers ------------------------ */

const clamp = (x: number, lo: number, hi: number) =>
    x < lo ? lo : x > hi ? hi : x;
const idx3 = (x: number, y: number, z: number, nx: number, ny: number) =>
    (z * ny + y) * nx + x;

function triLerpU8(
    data: Uint8Array,
    nx: number,
    ny: number,
    nz: number,
    x: number,
    y: number,
    z: number
): number {
    if (x < 0 || y < 0 || z < 0 || x > nx - 1 || y > ny - 1 || z > nz - 1) {
        return 0;
    }
    const x0 = Math.floor(x),
        y0 = Math.floor(y),
        z0 = Math.floor(z);
    const x1 = x0 + 1 < nx ? x0 + 1 : x0;
    const y1 = y0 + 1 < ny ? y0 + 1 : y0;
    const z1 = z0 + 1 < nz ? z0 + 1 : z0;
    const tx = x - x0,
        ty = y - y0,
        tz = z - z0;

    const i000 = idx3(x0, y0, z0, nx, ny),
        i100 = idx3(x1, y0, z0, nx, ny);
    const i010 = idx3(x0, y1, z0, nx, ny),
        i110 = idx3(x1, y1, z0, nx, ny);
    const i001 = idx3(x0, y0, z1, nx, ny),
        i101 = idx3(x1, y0, z1, nx, ny);
    const i011 = idx3(x0, y1, z1, nx, ny),
        i111 = idx3(x1, y1, z1, nx, ny);

    const c00 = data[i000] * (1 - tx) + data[i100] * tx;
    const c10 = data[i010] * (1 - tx) + data[i110] * tx;
    const c01 = data[i001] * (1 - tx) + data[i101] * tx;
    const c11 = data[i011] * (1 - tx) + data[i111] * tx;

    const c0 = c00 * (1 - ty) + c10 * ty;
    const c1 = c01 * (1 - ty) + c11 * ty;
    return c0 * (1 - tz) + c1 * tz;
}

function nearestMaskU8(
    data: Uint8Array,
    nx: number,
    ny: number,
    nz: number,
    x: number,
    y: number,
    z: number,
    thr: number
): number {
    const xi = clamp(Math.round(x), 0, nx - 1);
    const yi = clamp(Math.round(y), 0, ny - 1);
    const zi = clamp(Math.round(z), 0, nz - 1);
    return data[idx3(xi, yi, zi, nx, ny)] >= thr ? 1 : 0;
}

/** Add 'volume' uniformly across [d0,d1] into differential bins (binWidth,dMin). Ensures at least two bins if d0==d1. */
function addIntervalVolume(
    diff: Float64Array,
    volume: number,
    d0: number,
    d1: number,
    dMin: number,
    binWidth: number
) {
    if (!isFinite(d0) || !isFinite(d1) || volume <= 0) {
        return;
    }

    let a = Math.min(d0, d1),
        b = Math.max(d0, d1);
    // ensure at least one bin gap when equal
    if (Math.abs(b - a) < 1e-9) {
        a -= 0.5 * binWidth;
        b += 0.5 * binWidth;
    }

    const invW = 1 / binWidth;
    const nBins = diff.length;
    const firstBin = Math.max(0, Math.floor((a - dMin) * invW));
    const lastBin = Math.max(0, Math.floor((b - dMin) * invW));
    const L = Math.max(1e-12, b - a);

    for (let bin = firstBin; bin <= lastBin && bin < nBins; bin++) {
        const left = dMin + bin * binWidth;
        const right = left + binWidth;
        const overlap = Math.max(0, Math.min(b, right) - Math.max(a, left));
        if (overlap > 0) {
            diff[bin] += volume * (overlap / L);
        }
    }
}

function parallelepipedVolume(Wx: vec3, Wy: vec3, Wz: vec3): number {
    const c = vec3.create();
    vec3.cross(c, Wy, Wz);
    return Math.abs(vec3.dot(Wx, c));
}

/** dose index -> SDF index combined matrix: C = w2i_sdf * i2w_dose */
function buildDoseIdxToSdfIdx(dose: GridXform, sdf: GridXform): mat4 {
    const C = mat4.create();
    mat4.multiply(C, sdf.w2i, dose.i2w);
    return C; // column-major: p = i*col0 + j*col1 + k*col2 + col3
}

/** Overlap AABB in dose index space by transforming SDF box corners (with ±0.5 pad) into dose index. */
function overlapDoseIndexBounds(dose: GridXform, sdf: GridXform) {
    const [nxS, nyS, nzS] = sdf.dims;
    const S2D = mat4.create();
    mat4.multiply(S2D, dose.w2i, sdf.i2w);

    const corners: [number, number, number][] = [];
    const xs = [-0.5, nxS - 0.5],
        ys = [-0.5, nyS - 0.5],
        zs = [-0.5, nzS - 0.5];
    for (const x of xs) {
        for (const y of ys) {
            for (const z of zs) {
                corners.push([x, y, z]);
            }
        }
    }

    let minI = +1e30,
        minJ = +1e30,
        minK = +1e30,
        maxI = -1e30,
        maxJ = -1e30,
        maxK = -1e30;
    const tmp = vec3.create();
    for (const [x, y, z] of corners) {
        vec3.set(tmp, x, y, z);
        vec3.transformMat4(tmp, tmp, S2D);
        if (tmp[0] < minI) {
            minI = tmp[0];
        }
        if (tmp[0] > maxI) {
            maxI = tmp[0];
        }
        if (tmp[1] < minJ) {
            minJ = tmp[1];
        }
        if (tmp[1] > maxJ) {
            maxJ = tmp[1];
        }
        if (tmp[2] < minK) {
            minK = tmp[2];
        }
        if (tmp[2] > maxK) {
            maxK = tmp[2];
        }
    }

    const [nxD, nyD, nzD] = dose.dims;
    const i0 = clamp(Math.floor(minI), 0, nxD - 1);
    const i1 = clamp(Math.ceil(maxI), 0, nxD - 1);
    const j0 = clamp(Math.floor(minJ), 0, nyD - 1);
    const j1 = clamp(Math.ceil(maxJ), 0, nyD - 1);
    const k0 = clamp(Math.floor(minK), 0, nzD - 1);
    const k1 = clamp(Math.ceil(maxK), 0, nzD - 1);
    return { i0, i1, j0, j1, k0, k1 };
}

/* ------------------------ optimized DVH ------------------------ */

export async function calculateDvh(
    dose: DoseMatrix,
    structure: RegionOfInterest
): Promise<DVHResult> {
    if (structure.isEmpty || !structure.texture) {
        return emptyDVHResult();
    }
    const roiPixels = structure.texture?.pixels;
    if (!roiPixels) {
        return emptyDVHResult();
    }
    const textureGrid = structure.texture.textureGrid;
    const structureGrid = makeGridXform(
        [textureGrid.sizeX, textureGrid.sizeY, textureGrid.sizeZ],
        textureGrid.getCornerVoxelCenterPosition(),
        textureGrid.getRes(),
        textureGrid.getUnitX(),
        textureGrid.getUnitY(),
        textureGrid.getUnitZ()
    );
    const singleArray = new Uint8Array(
        structureGrid.dims[0] * structureGrid.dims[1] * structureGrid.dims[2]
    );
    let idx = 0;
    for (const slice of roiPixels) {
        singleArray.set(slice, idx);
        idx += slice.length;
    }

    const dosePixels = dose.realPixels;
    const doseGrid = makeGridXform(
        [dose.grid.sizeX, dose.grid.sizeY, dose.grid.sizeZ],
        dose.grid.getCornerVoxelCenterPosition(),
        dose.grid.getRes(),
        dose.grid.getUnitX(),
        dose.grid.getUnitY(),
        dose.grid.getUnitZ()
    );

    return computeDVH(dosePixels, doseGrid, singleArray, structureGrid, {
        threshold: 127.5,
        binWidthGy: 0.05,
        doseMinGy: 0,
        doseMaxGy: Math.ceil(dose.doseMaxGy),
    });
}

function nextFrame(): Promise<void> {
    return new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
    });
}

export async function computeDVH(
    dose: Float32Array,
    doseGrid: GridXform,
    sdf: Uint8Array,
    sdfGrid: GridXform,
    opts?: DVHOptions
): Promise<DVHResult> {
    const [nxD, nyD, nzD] = doseGrid.dims;
    const N = nxD * nyD * nzD;
    if (dose.length !== N) {
        throw new Error('Dose length != dose grid product');
    }

    const thr = opts?.threshold ?? 127.5;
    const edgeBand = opts?.edgeBand ?? 6; // U8 units (~2.35% of full scale)

    const binWidth = opts?.binWidthGy ?? 0.1;
    const invBinWidth = 1 / binWidth;
    const dMin = opts?.doseMinGy ?? 0;

    let dMax = opts?.doseMaxGy;
    if (dMax == null) {
        let mx = -Infinity;
        for (let i = 0; i < N; i++) {
            if (dose[i] > mx) {
                mx = dose[i];
            }
        }
        dMax = Math.max(0, mx);
    }
    const nBins = Math.max(1, Math.ceil((dMax - dMin) * invBinWidth) + 1);

    // Precompute dose step vectors in world & volume
    const Wx = vec3.fromValues(
        doseGrid.i2w[0],
        doseGrid.i2w[1],
        doseGrid.i2w[2]
    );
    const Wy = vec3.fromValues(
        doseGrid.i2w[4],
        doseGrid.i2w[5],
        doseGrid.i2w[6]
    );
    const Wz = vec3.fromValues(
        doseGrid.i2w[8],
        doseGrid.i2w[9],
        doseGrid.i2w[10]
    );
    const doseVoxelVol = parallelepipedVolume(Wx, Wy, Wz);

    // Combined transform for fast stepping in SDF index space
    const C = buildDoseIdxToSdfIdx(doseGrid, sdfGrid);
    const uX = vec3.fromValues(C[0], C[1], C[2]);
    const uY = vec3.fromValues(C[4], C[5], C[6]);
    const uZ = vec3.fromValues(C[8], C[9], C[10]);
    const t = vec3.fromValues(C[12], C[13], C[14]);

    // Half steps in SDF index for face/corner evaluation
    const halfUx = vec3.scale(vec3.create(), uX, 0.5);
    const halfUy = vec3.scale(vec3.create(), uY, 0.5);
    const halfUz = vec3.scale(vec3.create(), uZ, 0.5);

    // Crop iteration to SDF overlap
    const { i0, i1, j0, j1, k0, k1 } = overlapDoseIndexBounds(
        doseGrid,
        sdfGrid
    );

    const [nxS, nyS, nzS] = sdfGrid.dims;
    const diff = new Float64Array(nBins + 1);

    // Axis lengths (world) for separable PV
    const Lx = vec3.length(Wx),
        Ly = vec3.length(Wy),
        Lz = vec3.length(Wz);

    let structureVolume = 0;
    let doseWeightedVolume = 0; // NEW: exact accumulation of dose*volume
    let maxDoseFound = 0,
        minDoseFound = Infinity;

    // Starting SDF index position for (i0,j0,k0)
    // p0 = t + i0*uX + j0*uY + k0*uZ
    let p0x = t[0] + i0 * uX[0] + j0 * uY[0] + k0 * uZ[0];
    let p0y = t[1] + i0 * uX[1] + j0 * uY[1] + k0 * uZ[1];
    let p0z = t[2] + i0 * uX[2] + j0 * uY[2] + k0 * uZ[2];

    // Yield control periodically to avoid blocking the main thread
    const YIELD_EVERY_MS = 12; // ~one yield per frame at 60 Hz
    let lastYield = performance.now();

    for (let k = k0; k <= k1; k++) {
        // start of slice k
        let pRowStartX = p0x,
            pRowStartY = p0y,
            pRowStartZ = p0z;
        for (let j = j0; j <= j1; j++) {
            // start of row (k,j)
            let px = pRowStartX,
                py = pRowStartY,
                pz = pRowStartZ;
            let pos = idx3(i0, j, k, nxD, nyD);
            for (let i = i0; i <= i1; i++, pos++) {
                // Quick center NN classification → skip fast
                const centerNN = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px,
                    py,
                    pz,
                    thr
                );
                if (!centerNN) {
                    // Far outside? band check
                    const vCenter = triLerpU8(sdf, nxS, nyS, nzS, px, py, pz);
                    if (vCenter < thr - edgeBand) {
                        px += uX[0];
                        py += uX[1];
                        pz += uX[2];
                        continue;
                    }
                }

                // Decide partial volume
                let frac = 0;
                // Face points in SDF index space (use NN first)
                const x0i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px - halfUx[0],
                    py - halfUx[1],
                    pz - halfUx[2],
                    thr
                );
                const x1i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px + halfUx[0],
                    py + halfUx[1],
                    pz + halfUx[2],
                    thr
                );
                const y0i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px - halfUy[0],
                    py - halfUy[1],
                    pz - halfUy[2],
                    thr
                );
                const y1i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px + halfUy[0],
                    py + halfUy[1],
                    pz + halfUy[2],
                    thr
                );
                const z0i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px - halfUz[0],
                    py - halfUz[1],
                    pz - halfUz[2],
                    thr
                );
                const z1i = nearestMaskU8(
                    sdf,
                    nxS,
                    nyS,
                    nzS,
                    px + halfUz[0],
                    py + halfUz[1],
                    pz + halfUz[2],
                    thr
                );

                const len1D = (
                    insideA: number,
                    insideB: number,
                    full: number,
                    ax: number,
                    ay: number,
                    az: number
                ) => {
                    if (insideA && insideB) {
                        return full;
                    }
                    if (!insideA && !insideB) {
                        return 0;
                    }
                    // need precise crossing only if mixed:
                    const a = triLerpU8(
                        sdf,
                        nxS,
                        nyS,
                        nzS,
                        px - ax,
                        py - ay,
                        pz - az
                    );
                    const b = triLerpU8(
                        sdf,
                        nxS,
                        nyS,
                        nzS,
                        px + ax,
                        py + ay,
                        pz + az
                    );
                    const t = (thr - a) / (b - a);
                    const tc = t < 0 ? 0 : t > 1 ? 1 : t;
                    return insideA ? tc * full : (1 - tc) * full;
                };
                const Lxi = len1D(
                    x0i,
                    x1i,
                    Lx,
                    halfUx[0],
                    halfUx[1],
                    halfUx[2]
                );
                const Lyi = len1D(
                    y0i,
                    y1i,
                    Ly,
                    halfUy[0],
                    halfUy[1],
                    halfUy[2]
                );
                const Lzi = len1D(
                    z0i,
                    z1i,
                    Lz,
                    halfUz[0],
                    halfUz[1],
                    halfUz[2]
                );

                frac =
                    (Lxi / (Lx || 1)) * (Lyi / (Ly || 1)) * (Lzi / (Lz || 1));

                if (frac > 0) {
                    const s0 = triLerpU8(sdf, nxS, nyS, nzS, px, py, pz);
                    const s1 = triLerpU8(
                        sdf,
                        nxS,
                        nyS,
                        nzS,
                        px + uX[0],
                        py + uX[1],
                        pz + uX[2]
                    );
                    const d0 = dose[pos];
                    const d1 = i + 1 <= i1 ? dose[pos + 1] : d0;

                    if (d0 > maxDoseFound) {
                        maxDoseFound = d0;
                    }
                    if (d0 < minDoseFound) {
                        minDoseFound = d0;
                    }
                    if (d1 > maxDoseFound) {
                        maxDoseFound = d1;
                    }
                    if (d1 < minDoseFound) {
                        minDoseFound = d1;
                    }

                    let Lxi = 0,
                        Dstart = 0,
                        Dend = 0;
                    if (s0 < thr - edgeBand && s1 < thr - edgeBand) {
                        Lxi = 0;
                    } else if (s0 >= thr + edgeBand && s1 >= thr + edgeBand) {
                        Lxi = Lx;
                        Dstart = d0;
                        Dend = d1;
                    } else {
                        const denom = s1 - s0;
                        const tCross = denom !== 0 ? (thr - s0) / denom : 0.5;
                        const tc = tCross < 0 ? 0 : tCross > 1 ? 1 : tCross;
                        if (s0 < thr && s1 >= thr) {
                            Lxi = (1 - tc) * Lx;
                            Dstart = d0 + (d1 - d0) * tc;
                            Dend = d1;
                        } else if (s0 >= thr && s1 < thr) {
                            Lxi = tc * Lx;
                            Dstart = d0;
                            Dend = d0 + (d1 - d0) * tc;
                        } else {
                            const centerNN2 = nearestMaskU8(
                                sdf,
                                nxS,
                                nyS,
                                nzS,
                                px + 0.5 * uX[0],
                                py + 0.5 * uX[1],
                                pz + 0.5 * uX[2],
                                thr
                            );
                            if (centerNN2) {
                                Lxi = Lx;
                                Dstart = d0;
                                Dend = d1;
                            } else {
                                Lxi = 0;
                            }
                        }
                    }

                    const fracPV =
                        (Lxi / (Lx || 1)) *
                        (Lyi / (Ly || 1)) *
                        (Lzi / (Lz || 1));
                    if (fracPV > 0) {
                        const w = fracPV * doseVoxelVol;
                        structureVolume += w;

                        // NEW: exact dose accumulation using average interval dose
                        const avgDose = 0.5 * (Dstart + Dend);
                        doseWeightedVolume += w * avgDose;

                        // DVH bin population (kept as before)
                        addIntervalVolume(
                            diff,
                            w,
                            Dstart,
                            Dend,
                            dMin,
                            binWidth
                        );
                    }
                }

                px += uX[0];
                py += uX[1];
                pz += uX[2];
            }
            pRowStartX += uY[0];
            pRowStartY += uY[1];
            pRowStartZ += uY[2];
        }

        const now = performance.now();
        if (now - lastYield >= YIELD_EVERY_MS) {
            await nextFrame();
            lastYield = performance.now();
        }

        p0x += uZ[0];
        p0y += uZ[1];
        p0z += uZ[2];
    }

    const scaleToRel = structureVolume > 0 ? 100 / structureVolume : 0;
    const cum = new Float64Array(nBins + 1);
    let run = 0;
    for (let b = nBins - 1; b >= 0; b--) {
        run += diff[b];
        cum[b] = run * scaleToRel;
    }

    return {
        binWidthGy: binWidth,
        doseMinGy: minDoseFound < Infinity ? minDoseFound : 0,
        doseMaxGy: maxDoseFound,
        doseMeanGy:
            structureVolume > 0 ? doseWeightedVolume / structureVolume : 0,
        cumDvhRelative: cum,
        structureVolume_mm3: structureVolume,
    };
}
